import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Formik, FormikValues } from 'formik';
import * as Yup from 'yup';

import createStudent from '../../../lib/apiClient/students/create';
import { selectGrades } from '../../../redux/reducers/GradesReducer';

import Button from '../../common/ui/Button/Button';
import PopupForm from '../../common/ui/PopupForm/Form';
import PopupFormHeader from '../../common/ui/PopupForm/Header';
import FormikInputField from '../../common/ui/FormikInputField/Input';
import PopupFormErrorMessage from '../../common/ui/PopupForm/ErrorMessage';
import Select from '../../common/ui/Select/Select';

interface Props {
  onClose: () => void;
}

const CreateStudentForm = ({ onClose }: Props) => {
  const [error, setError] = useState<string | null>(null);

  const grades = Object.values(useSelector(selectGrades));
  const gradesNames = Array.from(new Set(grades.map(({ name }) => name)));

  const initialValues = {
    username: '',
    password: '',
    sex: '',
    age: '',
    gradeName: '',
    gradeLetter: '',
  };

  const validationSchema = Yup.object({
    username: Yup.string()
      .min(4, 'Минимальная длина - 4 символа')
      .max(255, 'Максимальная длина - 255 символов')
      .required('Обязательное поле'),
    password: Yup.string()
      .min(6, 'Минимальная длина - 6 символов')
      .max(255, 'Максимальная длина - 255 символов')
      .required('Обязательное поле'),
    sex: Yup.string()
      .oneOf(['м', 'ж'], 'Пол может быть одним из двух: [м, ж]')
      .required('Обязательное поле'),
    age: Yup.number()
      .positive('Возраст не может быть отрицательным')
      .required('Обязательное поле'),
    gradeName: Yup.string()
      .oneOf(gradesNames, 'Указанного класса не существует')
      .required('Обязательное поле'),
    gradeLetter: Yup.string()
      .matches(/^[А-Я]|$/, 'Укажите заглавную букву русского алфавита'),
  });

  const submit = async ({
    username,
    password,
    sex,
    age,
    gradeName,
    gradeLetter,
    orphan,
    visionImpairment,
    hearingImpairment,
    musculoskeletalImpairment,
  }: FormikValues) => {
    try {
      const gradeId = grades.find(
        ({ name, letter }) => (name === gradeName && letter === gradeLetter)
      )?.id ?? -1;
      await createStudent({ 
        username, 
        password, 
        sex, 
        age, 
        gradeId, 
        orphan: { 'Да': true, 'Нет': false }[orphan as 'Да' | 'Нет'] ?? false, 
        visionImpairment: { 'Да': true, 'Нет': false }[visionImpairment as 'Да' | 'Нет'] ?? false, 
        hearingImpairment: { 'Да': true, 'Нет': false }[hearingImpairment as 'Да' | 'Нет'] ?? false, 
        musculoskeletalImpairment: { 'Да': true, 'Нет': false }[musculoskeletalImpairment as 'Да' | 'Нет'] ?? false, 
      });
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={submit}
    >
      <PopupForm close={onClose} closable>
        <PopupFormHeader>
          Добавление ученика
          <PopupFormErrorMessage text={error ?? ''} visible={error !== null} />
        </PopupFormHeader>

        <FormikInputField
          name="username"
          placeholder="Имя пользователя"
          onFocus={() => setError(null)}
        />

        <FormikInputField
          name="password"
          type="password"
          placeholder="Пароль"
          onFocus={() => setError(null)}
        />

        <Select
          name="sex"
          placeholder="Пол"
          options={['м', 'ж']}
          onFocus={() => setError(null)}
        />

        <FormikInputField
          name="age"
          type="number"
          placeholder="Возраст"
          onFocus={() => setError(null)}
        />

        <Select
          name="gradeName"
          placeholder="Класс"
          options={gradesNames}
          onFocus={() => setError(null)}
        />

        <FormikInputField
          name="gradeLetter"
          placeholder="Буква класса"
          onFocus={() => setError(null)}
        />

        <Select
          name="orphan"
          placeholder="Сирота"
          options={['Да', 'Нет']}
          onFocus={() => setError(null)}
        />

        <Select
          name="visionImpairment"
          placeholder="НЗ"
          options={['Да', 'Нет']}
          onFocus={() => setError(null)}
        />

        <Select
          name="hearingImpairment"
          placeholder="НС"
          options={['Да', 'Нет']}
          onFocus={() => setError(null)}
        />

        <Select
          name="musculoskeletalImpairment"
          placeholder="НОДА"
          options={['Да', 'Нет']}
          onFocus={() => setError(null)}
        />

        <Button type="submit">Подтвердить</Button>
      </PopupForm>
    </Formik>
  );
};

export default CreateStudentForm;
